import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import pageMeta from '../../page-meta.json';
import { DEFAULT_LANG, localizePath, stripLangPrefix, type Lang } from '../utils/locale';

const PAGE_META = pageMeta as Record<string, unknown>;

/** page-meta.json のキーは素のパスなので、フラグメント・クエリ・末尾スラッシュを落とす */
const pageKeyOf = (to: string) => to.split(/[?#]/)[0].replace(/(.)\/$/, '$1');

/** そのパスの翻訳版が実際に配信されているか。未登録なら CDN が 404 を返すので出さない */
export const hasTranslation = (path: string, lang: Lang): boolean =>
  Object.prototype.hasOwnProperty.call(PAGE_META, localizePath(pageKeyOf(path), lang));

/**
 * 遷移先が日本語しか無い間だけ添える目印。Link が既定で末尾に置くが、
 * 見出しと説明を持つリンクでは呼び出し側が見出しの隣に置ける
 */
export const LangHint: React.FC<{ to: string }> = ({ to }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language as Lang) ?? DEFAULT_LANG;

  if (lang === DEFAULT_LANG || hasTranslation(to, lang)) return null;
  return <span className="opacity-70"> {t('ui.japaneseOnly')}</span>;
};

interface PublishedLinkProps {
  to: string;
  className?: string;
  onClick?: React.MouseEventHandler;
  /** 既定で末尾に出す。ロゴ画像を包むリンクや、LangHint を自分で置く場合に false */
  langHint?: boolean;
  children?: React.ReactNode;
}

/**
 * Router の basename により、英語ページの <Link to="/x"> は /en/x を指す。
 * その URL がまだ用意されていない間は日本語版へ逃がす（SPA rewrite が無いので
 * 未登録パスは CDN が 404 を返す）。basename の外へ出るため <a> でフルロードする。
 */
export const Link: React.FC<PublishedLinkProps> = ({ to, langHint = true, children, ...rest }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language as Lang) ?? DEFAULT_LANG;

  if (hasTranslation(to, lang)) {
    return (
      <RouterLink to={to} {...rest}>
        {children}
      </RouterLink>
    );
  }

  return (
    <a href={stripLangPrefix(to)} hrefLang={DEFAULT_LANG} {...rest}>
      {children}
      {langHint && <LangHint to={to} />}
    </a>
  );
};
